import { cryptoStateAxiosApi } from 'src/Config/axiosApi';
import { Crypto, MarkAsReadCrypto } from 'src/Modules/Arbitrage/types';
import {
  ModifiedCrypto,
  ModifiedCryptoFormValues
} from 'src/Modules/ConfigEditor/types';
import * as endpoints from './endpoints';
import { MarkAsReadCryptoFormValues } from 'src/Modules/Arbitrage/context';

// ============================================
// Cryptos API - crypto-state-manager
// ============================================

interface CryptoResponse {
  _id?: string;
  id: string;
  name: string;
  symbol: string;
  slug: string;
  rank?: number;
  is_active?: boolean;
  isAdded?: boolean;
  platform?: {
    id?: string;
    name?: string;
    symbol?: string;
    slug?: string;
    token_address?: string;
  };
}

interface PaginatedCryptoResponse {
  data: CryptoResponse[];
  total: number;
  page: number;
  limit: number;
}

const transformCrypto = (item: CryptoResponse): Crypto => ({
  id: item.id || item._id || '',
  name: item.name,
  symbol: item.symbol,
  slug: item.slug,
  rank: item.rank ?? 0,
  is_active: item.is_active ? 1 : 0,
  isAdded: item.isAdded ?? false,
  platform: item.platform
    ? {
        id: Number(item.platform.id) || 0,
        name: item.platform.name || '',
        symbol: item.platform.symbol || '',
        slug: item.platform.slug || '',
        token_address: item.platform.token_address || ''
      }
    : null
});

export const searchCryptos = async (
  symbol: string,
  allCryptos = false
): Promise<Crypto[]> => {
  const res = await cryptoStateAxiosApi.get<PaginatedCryptoResponse>(
    `${endpoints.CRYPTOS}`,
    {
      params: {
        symbol,
        limit: 20,
        ...(allCryptos ? {} : { is_active: true })
      }
    }
  );
  return res.data.data.map(transformCrypto);
};

export const getCryptoById = async (id: string): Promise<Crypto> => {
  const res = await cryptoStateAxiosApi.get<CryptoResponse>(
    `${endpoints.CRYPTOS}/${id}`
  );
  return transformCrypto(res.data);
};

export interface CreateCryptoData {
  id?: string;
  name: string;
  symbol: string;
  slug?: string;
}

export const createCrypto = async (data: CreateCryptoData): Promise<Crypto> => {
  const res = await cryptoStateAxiosApi.post<CryptoResponse>(
    endpoints.CRYPTOS,
    {
      ...data,
      slug: data.slug || data.symbol.toLowerCase()
    }
  );
  return transformCrypto(res.data);
};

export const updateCrypto = async (
  id: string,
  data: Partial<CreateCryptoData>
): Promise<Crypto> => {
  const res = await cryptoStateAxiosApi.put<CryptoResponse>(
    `${endpoints.CRYPTOS}/${id}`,
    data
  );
  return transformCrypto(res.data);
};

export const syncCoinMarketCap = async (): Promise<{ message: string }> => {
  const res = await cryptoStateAxiosApi.post<{ message: string }>(
    `${endpoints.CRYPTOS}/sync-coinmarketcap`
  );
  return res.data;
};

// ============================================
// Read Cryptos API - crypto-state-manager
// ============================================

interface ReadCryptoResponse {
  _id: string;
  name: string;
  quoteCoin?: string;
  exchange: string;
  endDate: string;
  reason: string;
  volumeToRemoveMarkAsRead?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

const transformReadCrypto = (item: ReadCryptoResponse): MarkAsReadCrypto => ({
  id: item._id,
  name: item.name,
  quoteCoin: item.quoteCoin,
  exchange: item.exchange,
  endDate: item.endDate,
  reason: item.reason as MarkAsReadCrypto['reason'],
  volumeToRemoveMarkAsRead: item.volumeToRemoveMarkAsRead,
  crypto: {
    id: item._id,
    name: item.name,
    symbol: item.name,
    slug: item.name.toLowerCase()
  } as Crypto
});

export const getCryptosMarkedAsRead = async (): Promise<MarkAsReadCrypto[]> => {
  const PAGE_SIZE = 100;

  // First request to get total count
  const firstRes = await cryptoStateAxiosApi.get<
    PaginatedResponse<ReadCryptoResponse>
  >(`${endpoints.READ_CRYPTOS}?limit=${PAGE_SIZE}&page=0`);

  const { data: firstPageData, total } = firstRes.data;
  const allData: ReadCryptoResponse[] = [...firstPageData];

  // Calculate remaining pages
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (totalPages > 1) {
    // Fetch remaining pages in parallel
    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, i) => i + 1
    );
    const pagePromises = remainingPages.map(page =>
      cryptoStateAxiosApi.get<PaginatedResponse<ReadCryptoResponse>>(
        `${endpoints.READ_CRYPTOS}?limit=${PAGE_SIZE}&page=${page}`
      )
    );

    const results = await Promise.all(pagePromises);
    results.forEach(res => allData.push(...res.data.data));
  }

  return allData.map(transformReadCrypto);
};

export const postCryptoMarkedAsRead = async (
  data: MarkAsReadCryptoFormValues
) => {
  const res = await cryptoStateAxiosApi.post<ReadCryptoResponse>(
    endpoints.READ_CRYPTOS,
    data
  );
  return transformReadCrypto(res.data);
};

export const putCryptoMarkedAsRead = async (
  id: string,
  data: MarkAsReadCryptoFormValues
) => {
  const res = await cryptoStateAxiosApi.patch<ReadCryptoResponse>(
    `${endpoints.READ_CRYPTOS}/${id}`,
    data
  );
  return transformReadCrypto(res.data);
};

export const deleteCryptoMarkedAsRead = async (id: string) => {
  await cryptoStateAxiosApi.delete(`${endpoints.READ_CRYPTOS}/${id}`);
  return { id };
};

// ============================================
// Modified Cryptos API - crypto-state-manager
// ============================================

interface ModifiedCryptoResponse {
  _id: string;
  exchange: string;
  symbol: string;
  cryptoId: string;
  address?: string;
  network?: string;
  notes?: string;
  isActive?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  cryptoTarget?: CryptoResponse | null;
}

const transformModifiedCrypto = (
  item: ModifiedCryptoResponse
): ModifiedCrypto => ({
  id: item._id,
  exchange: item.exchange,
  symbol: item.symbol,
  cryptoId: item.cryptoId,
  createdAt: item.createdAt || new Date().toISOString(),
  updatedAt: item.updatedAt || new Date().toISOString(),
  cryptoTarget: item.cryptoTarget
    ? transformCrypto(item.cryptoTarget)
    : {
        id: item.cryptoId,
        name: item.symbol,
        symbol: item.symbol,
        slug: '',
        rank: 0,
        is_active: 0,
        isAdded: true,
        platform: null
      }
});

export const getCryptosModified = async (): Promise<ModifiedCrypto[]> => {
  const PAGE_SIZE = 100;

  // First request to get total count
  const firstRes = await cryptoStateAxiosApi.get<
    PaginatedResponse<ModifiedCryptoResponse>
  >(`${endpoints.MODIFIED_CRYPTOS}?limit=${PAGE_SIZE}&page=0`);

  const { data: firstPageData, total } = firstRes.data;
  const allData: ModifiedCryptoResponse[] = [...firstPageData];

  // Calculate remaining pages
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (totalPages > 1) {
    // Fetch remaining pages in parallel
    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, i) => i + 1
    );
    const pagePromises = remainingPages.map(page =>
      cryptoStateAxiosApi.get<PaginatedResponse<ModifiedCryptoResponse>>(
        `${endpoints.MODIFIED_CRYPTOS}?limit=${PAGE_SIZE}&page=${page}`
      )
    );

    const results = await Promise.all(pagePromises);
    results.forEach(res => allData.push(...res.data.data));
  }

  return allData.map(transformModifiedCrypto);
};

export const postCryptosModified = async (
  data: ModifiedCryptoFormValues
): Promise<ModifiedCrypto> => {
  const { exchanges, ...rest } = data;
  const results = await Promise.all(
    exchanges.map(exchange =>
      cryptoStateAxiosApi.post<ModifiedCryptoResponse>(
        endpoints.MODIFIED_CRYPTOS,
        { ...rest, exchange }
      )
    )
  );
  return transformModifiedCrypto(results[results.length - 1].data);
};

export const putCryptosModified = async (
  id: ModifiedCrypto['id'],
  data: ModifiedCrypto
): Promise<ModifiedCrypto> => {
  const res = await cryptoStateAxiosApi.patch<ModifiedCryptoResponse>(
    `${endpoints.MODIFIED_CRYPTOS}/${id}`,
    data
  );
  return transformModifiedCrypto(res.data);
};

export const deleteCryptosModified = async (
  id: ModifiedCrypto['id']
): Promise<ModifiedCrypto> => {
  const res = await cryptoStateAxiosApi.delete<ModifiedCryptoResponse>(
    `${endpoints.MODIFIED_CRYPTOS}/${id}`
  );
  return transformModifiedCrypto(res.data);
};
